import { type RefObject, useCallback, useMemo } from "react";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
    SortableContext,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import type { ColumnPin, View } from "@common/types/views/views";
import { SettingsPanel } from "@design-system/SettingsPanel/SettingsPanel.lib";
import { ColumnDragOverlay } from "./ColumnDragOverlay";
import { ColumnItem, ColumnItemOverlay } from "./ColumnItem";
import { ColumnSection } from "./ColumnSection";
import {
    type ColumnData,
    type ColumnSettingsConfig,
    separateColumnsByPin,
    withPinsKept,
} from "./columnSettingsUtils";
import { useColumnSettingsRows } from "./useColumnSettingsRows";
import { useDragAndDropColumns } from "./useDragAndDropColumns";

export type { ColumnSettingsConfig } from "./columnSettingsUtils";

/** The two sections' titles and what each says when it's empty. */
export interface ColumnSettingsListCopy {
    shownTitle: string;
    hiddenTitle: string;
    noneShown: string;
    noneHidden: string;
}

interface ColumnSettingsListProps {
    /** Every column of the view, in order. */
    columns: View["columns"];
    /** The columns the list offers, by id; hidden ones list in this order. */
    columnConfigs: Readonly<Record<string, ColumnSettingsConfig>>;
    onColumnsChange: (columns: View["columns"]) => void;
    copy: ColumnSettingsListCopy;
    /** With it, shown rows get the pin menu; without it, a drag keeps each row's pin. */
    onPin?: (columnId: string, pin: ColumnPin | undefined) => void;
    /** The popover's scroll container, the only thing a drag scrolls. */
    scrollContainerRef: RefObject<HTMLElement | null>;
    /**
     * Rows whose place isn't set by order: listed first in their section, in
     * this order, with no drag handle, and other rows don't drop among them.
     */
    fixedColumnIds?: ReadonlySet<string>;
}

const NO_FIXED_COLUMN_IDS: ReadonlySet<string> = new Set();

/** Rows that don't drag: the hidden ones, and the fixed ones in either section. */
const StaticColumnRows = ({
    columns,
    isVisible,
    isDragInProgress,
    onToggleVisibility,
}: Readonly<{
    columns: ColumnData[];
    isVisible: boolean;
    isDragInProgress: boolean;
    onToggleVisibility: (columnId: string) => void;
}>) =>
    columns.map((column) => (
        <ColumnItem
            key={column.id}
            id={column.id}
            name={column.name}
            hint={column.hint}
            isVisible={isVisible}
            immutable={column.immutable}
            required={column.required}
            isDraggable={false}
            isDragInProgress={isDragInProgress}
            onToggleVisibility={() => onToggleVisibility(column.id)}
        />
    ));

/**
 * A view's columns as column settings rows: the shown ones in order, each
 * with an eye to hide it and a handle to drag it into place, then the hidden
 * ones with an eye to show them, which adds them at the end. Fixed rows lead
 * each section and only have the eye.
 */
export const ColumnSettingsList = ({
    columns,
    columnConfigs,
    onColumnsChange,
    copy,
    onPin,
    scrollContainerRef,
    fixedColumnIds = NO_FIXED_COLUMN_IDS,
}: Readonly<ColumnSettingsListProps>) => {
    const {
        hasShownColumns,
        hasHiddenColumns,
        shown,
        hidden,
        toggleVisibility,
    } = useColumnSettingsRows({
        columns,
        columnConfigs,
        fixedColumnIds,
        onColumnsChange,
    });

    const handleDragColumnsChange = useCallback(
        (next: View["columns"]) =>
            onColumnsChange(
                onPin ? next : withPinsKept({ previous: columns, next }),
            ),
        [columns, onColumnsChange, onPin],
    );

    const {
        activeId,
        sensors,
        handleDragStart,
        handleDragEnd,
        handleDragCancel,
    } = useDragAndDropColumns({
        columns,
        visibleColumns: shown.orderedColumns,
        onColumnsChange: handleDragColumnsChange,
    });

    const { leftPinnedColumns, centerColumns, rightPinnedColumns } = useMemo(
        () => separateColumnsByPin(shown.orderedColumns),
        [shown.orderedColumns],
    );

    const sortableItems = useMemo(
        () =>
            [...leftPinnedColumns, ...centerColumns, ...rightPinnedColumns].map(
                (column) => column.id,
            ),
        [leftPinnedColumns, centerColumns, rightPinnedColumns],
    );

    const activeColumn = shown.orderedColumns.find(
        (column) => column.id === activeId,
    );
    const isDragInProgress = activeId !== null;

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
            autoScroll={{
                // Restrict auto-scroll to the popover's scroll container so
                // drop targets below the fold remain reachable while dragging,
                // without scrolling the page behind the popover.
                canScroll: (element) => element === scrollContainerRef.current,
            }}
        >
            <SettingsPanel.Section label={copy.shownTitle}>
                <SortableContext
                    items={sortableItems}
                    strategy={verticalListSortingStrategy}
                >
                    <StaticColumnRows
                        columns={shown.fixedColumns}
                        isVisible
                        isDragInProgress={isDragInProgress}
                        onToggleVisibility={toggleVisibility}
                    />
                    <ColumnSection
                        columns={leftPinnedColumns}
                        activeId={activeId}
                        onToggleVisibility={toggleVisibility}
                        onPin={onPin}
                    />
                    <ColumnSection
                        columns={centerColumns}
                        activeId={activeId}
                        onToggleVisibility={toggleVisibility}
                        onPin={onPin}
                    />
                    <ColumnSection
                        columns={rightPinnedColumns}
                        activeId={activeId}
                        onToggleVisibility={toggleVisibility}
                        onPin={onPin}
                    />
                    {!hasShownColumns && (
                        <SettingsPanel.Empty>
                            {copy.noneShown}
                        </SettingsPanel.Empty>
                    )}
                </SortableContext>
            </SettingsPanel.Section>
            <SettingsPanel.Separator />
            <SettingsPanel.Section label={copy.hiddenTitle}>
                <StaticColumnRows
                    columns={[...hidden.fixedColumns, ...hidden.orderedColumns]}
                    isVisible={false}
                    isDragInProgress={isDragInProgress}
                    onToggleVisibility={toggleVisibility}
                />
                {!hasHiddenColumns && (
                    <SettingsPanel.Empty>{copy.noneHidden}</SettingsPanel.Empty>
                )}
            </SettingsPanel.Section>
            <ColumnDragOverlay>
                {activeColumn && (
                    <ColumnItemOverlay
                        name={activeColumn.name}
                        hint={activeColumn.hint}
                        isPinned={activeColumn.options?.pin}
                        isLocked={activeColumn.required ?? false}
                        hasPinMenu={onPin !== undefined}
                    />
                )}
            </ColumnDragOverlay>
        </DndContext>
    );
};
